import { FileModel } from "interfaces/models";
import {
  DATA_TYPE_IMAGE_KEYS,
  FileDTO,
  FileHistory,
} from "interfaces/models/pin";
import { omit, pick } from "lodash-es";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { usePeripheral } from "../../context/PeripheralProvider";
import { RootState } from "redux/store";
import { useSelector } from "react-redux";
import { FieldPreviewProps, OnChangeType } from "../types";
import { useBoolean } from "@chakra-ui/react";
import { getDateTimeOriginalExif } from "utils/file";
import { getImageAfterDeletePhotoHistory } from "models/pin";
import { ItemData } from "components/containers/inspectionDetail/content/inspectionDocument/containers/gallery/Item";
import { ImageHasBlackboardValue } from "../imageHasBlackboard/previewer";
import networkMonitor from "libs/network-monitor/NetworkMonitor";
import { uuid } from "utils/common";

export enum ModalType {
  CAMERA = "CAMERA",
  CONFIRM_DELETE = "CONFIRM_DELETE",
  DRAW_IMAGE = "DRAW_IMAGE",
  GALLERY = "GALLERY",
}

export interface ImageValue extends FileModel {}

interface UseFieldImageProps extends FieldPreviewProps<ImageValue> {
  isBlackboard: boolean;
}

const useFieldImage = ({
  value,
  isLoading,
  isReadOnly,
  isDisabled,
  item,
  isBlackboard,
  onChange,
}: UseFieldImageProps) => {
  const [isImageError, imageError] = useBoolean();
  const [modal, setModal] = useState<ModalType | null>(null);
  const [image, setImage] = useState<
    (ImageValue & ImageHasBlackboardValue) | undefined
  >(value);
  const imageCaptured = useRef<File>();
  const disabled = isLoading || isReadOnly || isDisabled;
  const isOpenImageForm = !image?.src;
  const { pinSelected } = useSelector((state: RootState) => state.pin);
  const { camera, onCamera: onReceiveCamera } = usePeripheral();

  const onOpenPhotoHistories = useCallback(() => {
    setModal(ModalType.GALLERY);
  }, []);
  const onOpenDrawToolModal = useCallback(async (file?: FileDTO) => {
    if (file) {
      const dateTimeOriginal = await getDateTimeOriginalExif(file);
      file.lastModifiedDateFile = dateTimeOriginal ?? new Date();
    }

    imageCaptured.current = file;
    setModal(ModalType.DRAW_IMAGE);
  }, []);
  const imageProps = useMemo((): any => {
    return {
      ...image,
      boxProps: {
        cursor: disabled || isImageError ? "not-allowed" : "pointer",
        onError: imageError.on,
        onClick: () => {
          if (isLoading || disabled || isImageError) {
            return;
          }

          setModal(ModalType.DRAW_IMAGE);
        },
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isImageError, image, disabled, isLoading]);
  const onDeleteImageHistory = useCallback(
    async (itemDeleted: ItemData, histories: FileHistory[]) => {
      if (histories.length) {
        const newImage = getImageAfterDeletePhotoHistory({
          image: value,
          itemDeleted,
          histories,
        });
        if (!newImage) {
          return;
        }
        const { src, originSrc, uploadTime, lastModifiedDateFile } = newImage;

        const changePartials = (data: any | undefined) => ({
          ...(data || {}),
          src,
          originSrc,
          uploadTime,
          lastModifiedDateFile,
        });

        const result = await onChange?.(changePartials(value), {
          action: OnChangeType.DELETE_IMAGE,
        });

        if (result) {
          setImage((prev) => changePartials(prev));
        }

        return result;
      } else {
        await onChange?.(null, {
          action: OnChangeType.DELETE_IMAGE,
        });
        setImage(undefined);
      }
    },
    [value, onChange]
  );

  const onSelectFromGallery = useCallback(
    (history: FileHistory) => {
      if (!value) return;
      const uploadTime = history?.createdAt
        ? new Date(history?.createdAt)
        : undefined;

      const changePartials = <T extends boolean>(
        data: ImageValue | undefined,
        isBlackboard: T
      ): T extends true ? ImageHasBlackboardValue : ImageValue => {
        const baseData = {
          ...omit(data || {}, "createdAt"),
          originSrc: history?.originSrc ?? data?.originSrc,
          lastModifiedDateFile:
            history?.lastModifiedDateFile ?? data?.lastModifiedDateFile,
          src: history?.src,
          uploadTime,
        } as ImageValue;

        if (isBlackboard) {
          return {
            ...baseData,
            blackboardData: history.blackboardData,
          } as ImageHasBlackboardValue;
        }

        return baseData;
      };

      setImage((prev) => changePartials(prev, isBlackboard));
      onChange?.(changePartials(value, isBlackboard), {
        action: OnChangeType.CHANGE_IMAGE_FROM_GALLERY,
      });
      setModal(null);
    },
    [isBlackboard, value, onChange]
  );

  const onDraw = useCallback(
    async (file: any, isAddHistory: boolean = false, other: any = {}) => {
      setImage(file);

      const data = structuredClone(pick(file, DATA_TYPE_IMAGE_KEYS));
      // Support generating image history with IDs generated from the frontend
      if (!networkMonitor.isOnline() && isAddHistory) {
        data.offlineId = uuid();
      }

      onChange?.(
        {
          ...data,
          ...other,
        },
        {
          action: isAddHistory
            ? OnChangeType.ADD_NEW_IMAGE
            : OnChangeType.EDIT_IMAGE,
        }
      );
    },
    [onChange]
  );

  const onCamera = useCallback(async () => {
    if (!camera?.(item.id)) {
      return setModal(ModalType.CAMERA);
    }
  }, [camera, item.id]);

  useEffect(() => {
    setImage(value ?? {});
  }, [value]);

  useEffect(() => {
    const unsubscribe = onReceiveCamera?.(item.id, (file) => {
      file && onOpenDrawToolModal(file);
    });

    return () => {
      unsubscribe?.();
    };
  }, [onReceiveCamera, item.id, onChange, onOpenDrawToolModal]);

  return {
    imageProps,
    modal,
    setModal,
    image,
    setImage,
    imageCaptured,
    disabled,
    isOpenImageForm,
    onOpenPhotoHistories,
    onOpenDrawToolModal,
    onDeleteImageHistory,
    onSelectFromGallery,
    onDraw,
    onCamera,
    pinSelected,
  };
};

export default useFieldImage;
